import { useState, useEffect, useRef } from 'react';
import moment from 'moment/min/moment-with-locales';

// Components
import Scheduler, { DATE_FORMAT, SchedulerData } from '../Roster/RosterScheduler';
import { CellUnits, ViewTypes } from '../../../components/Scheduler';

// Utils
import { minutesToTimeInput } from '../../../utils/timeCalc';

const generateMemberEventsFromTemplates = (publishedTemplates) => {
    if (!Array.isArray(publishedTemplates)) return [];
    return publishedTemplates.flatMap((template) => {
        const { shift_data = {}, user_id } = template;
        const shiftDetails = template?.Roster_Template?.rules?.shiftDetails || {};
        const events = [];
        Object.entries(shift_data).forEach(([date, { shiftId }]) => {
            const shift = Object.values(shiftDetails).find((s) => s.id === shiftId);
            if (!shift || shift.start_time == null || shift.end_time == null) return;
            const formattedDate = moment(date).format('YYYY-MM-DD');
            const start = `${formattedDate} ${minutesToTimeInput(shift.start_time, false)}:00`;
            const end = `${formattedDate} ${minutesToTimeInput(shift.end_time, false)}:00`;
            events.push({
                id: `${user_id}-${shiftId}-${formattedDate}`,
                resourceId: user_id,
                title: shift.shift_code,
                start,
                end,
                startTime: shift.start_time,
                endTime: shift.end_time,
                for_date: date,
                shiftId,
                showPopover: true,
            });
        });
        return events;
    });
};

const customEventItemTemplate = (_, eventItem) => (
    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'black' }}>
        <div style={{ fontSize: 12 }}>{eventItem.title}</div>
    </div>
);

function RosterSchedulePreview({ publishedTemplates, start_date, end_date, currentUser, dateTimeRules, t }) {
    const [schedulerData, setSchedulerData] = useState(null);
    const [width, setWidth] = useState(0);
    const scheduleRef = useRef(null);

    const updateWidth = () => {
        if (scheduleRef.current) {
            setWidth(scheduleRef.current.offsetWidth);
        }
    };

    useEffect(() => {
        const getCustomDate = (schedulerData, num, date) => {
            const { viewType } = schedulerData;
            if (viewType === ViewTypes.Custom1) {
                return {
                    startDate: moment(start_date).format(DATE_FORMAT),
                    endDate: moment(end_date).format(DATE_FORMAT),
                    cellUnit: CellUnits.Day,
                };
            }
            const startDateStr = num === 0 ? date : schedulerData.localeMoment(date).add(2 * num, 'days').format(DATE_FORMAT);
            return {
                startDate: startDateStr,
                endDate: schedulerData.localeMoment(startDateStr).add(1, 'days').format(DATE_FORMAT),
                cellUnit: CellUnits.Hour,
            };
        };

        const events = generateMemberEventsFromTemplates(publishedTemplates);
        const data = new SchedulerData(
            moment().format('YYYY-MM-DD'),
            ViewTypes.Custom1,
            false,
            false,
            {
                headerEnabled: false,
                startResizable: false,
                endResizable: false,
                movable: false,
                creatable: false,
                resourceName: t('Employee'),
                nonAgendaOtherCellHeaderFormat: 'ddd | D',
                schedulerWidth: '78%',
                tableHeaderHeight: 75,
                eventItemHeight: 40,
                eventItemLineHeight: 40,
            },
            { getCustomDateFunc: getCustomDate }
        );
        data.localeMoment.locale('en');
        const memberName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim();
        data.setResources([{
            id: currentUser.id,
            name: memberName,
            employee_percent: currentUser.employee_percent || 0,
            work_time_agreement: currentUser.work_time_agreement || 0,
        }]);
        data.setEvents(events);
        setSchedulerData(data);
    }, [publishedTemplates, start_date, end_date]);

    useEffect(() => {
        if (schedulerData) updateWidth();
    }, [schedulerData]);

    if (!schedulerData) return null;

    return (
        <div ref={scheduleRef} id="Roster-Scheduler" style={{ marginTop: 15 }}>
            <div className="my-1 fw-bold">{t('Roster Shifts')}</div>
            <Scheduler
                width={width}
                schedulerData={schedulerData}
                eventItemTemplateResolver={customEventItemTemplate}
                dateTimeRules={dateTimeRules}
                t={t}
            />
        </div>
    );
}

export default RosterSchedulePreview;
